/**
 * InfoBanner
 *
 * Small dismissible, animated informational banner used in the verify
 * step. Shows the API provided message, a Resend control and a visual
 * progress bar backed by `secondsRemaining`/`totalSeconds`.
 */
"use client";

import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import type React from "react";
import { RESEND_OTP_INTERVAL_SECONDS } from "@/config";
import CButton from "@/Shared/CButton/CButton";

interface Props {
  message: string | null;
  onDismiss: () => void;
  onResend?: () => void;
  secondsRemaining?: number;
  loading?: boolean;
  totalSeconds?: number;
}

const InfoBanner: React.FC<Props> = ({
  message,
  onDismiss,
  onResend,
  secondsRemaining = 0,
  loading = false,
  totalSeconds = RESEND_OTP_INTERVAL_SECONDS,
}) => {
  const percent =
    totalSeconds > 0
      ? Math.round(
          ((totalSeconds - Math.max(0, secondsRemaining)) / totalSeconds) * 100,
        )
      : 0;

  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className="mb-2 rounded-md bg-gray-50 dark:bg-gray-800 p-2"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm text-muted-foreground">{message}</p>
            <div className="flex items-center gap-2">
              {onResend && (
                <CButton
                  aria-label="resend-otp"
                  disabled={secondsRemaining > 0 || loading}
                  onClick={onResend}
                  variant="link"
                  size="sm"
                  className={`${
                    secondsRemaining > 0 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {secondsRemaining > 0
                    ? `Resend in ${secondsRemaining}s`
                    : "Resend"}
                </CButton>
              )}
              <CButton
                aria-label="dismiss-info"
                onClick={onDismiss}
                variant="ghost"
                size="sm"
                className="p-1"
              >
                <X size={16} />
              </CButton>
            </div>
          </div>

          <div className="mt-2 h-2 bg-gray-200 rounded overflow-hidden">
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={totalSeconds}
              aria-valuenow={totalSeconds - secondsRemaining}
              className="h-2 bg-primary rounded"
              style={{ width: `${percent}%`, transition: "width 0.3s linear" }}
              data-testid="resend-progress"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InfoBanner;
